// src/pages/Accueil.js
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import '../styles/Accueil.css';
import AccueilGalerie from '../components/AccueilGalerie';
import VisiteWidget from '../components/Visite';
import DonationWidget from '../components/DonationWidget';

// Import des images et du PDF
import shakogrenadierImage from '../assets/sescollections/periode2/shakogrenadier2.JPG';
import pseudoImage from '../assets/transfert/PSEUDO_1.JPG';
import waterlooImage from '../assets/actualitesdumusee/expowaterloo1.jpg';
import waterlooPdf from '../assets/actualitesdumusee/expowaterloo1.pdf';
import logo from '../assets/logomusee.png';

const Accueil = () => {
  const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
  const [selectedPdfPath, setSelectedPdfPath] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const openPdfViewer = useCallback((pdfPath) => {
    if (isMobile) {
      window.open(pdfPath, '_blank');
    } else {
      setSelectedPdfPath(pdfPath);
      setPdfViewerVisible(true);
      document.body.style.overflow = 'hidden';
    }
  }, [isMobile]);

  const closePdfViewer = useCallback(() => {
    setPdfViewerVisible(false);
    setSelectedPdfPath(null);
    document.body.style.overflow = 'unset';
  }, []);

  const downloadPdf = () => {
    if (selectedPdfPath) {
      const link = document.createElement('a');
      link.href = selectedPdfPath;
      link.download = selectedPdfPath.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && pdfViewerVisible) {
        closePdfViewer();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [pdfViewerVisible, closePdfViewer]);

  return (
    <main>
<Helmet>
        {/* Balises de base */}
        <title>Musée des Troupes de Marine - Accueil</title>
        <meta name="description" content="Découvrez l'histoire et le patrimoine des Troupes de Marine. Explorez nos collections, participez à nos conférences et restez informé des actualités du musée." />
        <meta name="keywords" content="Musée des Troupes de Marine, histoire militaire, collections, conférences, actualités, patrimoine" />

        {/* Favicons pour tous les navigateurs et appareils */}
        {/* Assure-toi que ces fichiers (favicon.ico, apple-touch-icon.png, manifest.json) sont bien à la racine de ton dossier public */}
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/manifest.json" />

        {/* Open Graph (pour Facebook, LinkedIn, etc.) */}
        <meta property="og:title" content="Musée des Troupes de Marine - Accueil" />
        <meta property="og:description" content="Découvrez l'histoire et le patrimoine des Troupes de Marine. Explorez nos collections, participez à nos conférences et restez informé des actualités du musée." />
        {/* Très important : 'logo' doit être l'URL ABSOLUE et PUBLIQUE de ton image */}
        <meta property="og:image" content={logo} />
        <meta property="og:url" content="https://www.museedestroupesdemarine.fr/pages/Accueil" />
        <meta property="og:type" content="website" /> {/* Précise le type de contenu */}
        <meta property="og:locale" content="fr_FR" /> {/* Spécifie la locale */}

        {/* Twitter Card (pour Twitter) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Musée des Troupes de Marine - Accueil" />
        <meta name="twitter:description" content="Découvrez l'histoire et le patrimoine des Troupes de Marine. Explorez nos collections, participez à nos conférences et restez informé des actualités du musée." />
        <meta name="twitter:image" content={logo} /> {/* Utilise la même URL que og:image */}
        {/* <meta name="twitter:site" content="@NomDeTonCompteTwitter" /> Si tu as un compte Twitter pour le musée */}
        {/* <meta name="twitter:creator" content="@NomDeTonCompteTwitter" /> Pour l'auteur spécifique du contenu */}

        {/* Balises canoniques pour éviter le contenu dupliqué */}
        <link rel="canonical" href="https://www.museedestroupesdemarine.fr/pages/Accueil" />

        {/* Optimisation pour les mobiles (déjà dans index.html, mais bien d'avoir ici si besoin de override) */}
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" /> {/* Couleur de la barre d'adresse sur mobile */}

      </Helmet>

      <section className="hero fade-in">
        <h1>Musée des Troupes de Marine</h1>
        <p>Découvrez l'histoire et le patrimoine des Troupes de Marine</p>
      </section>
      <section className="content-section fade-in">
        <h2>Bienvenue</h2>
        <p>Bienvenue au Musée des Troupes de Marine. Explorez notre riche collection d'objets historiques, de documents et d'œuvres d'art qui témoignent de l'histoire des Troupes de Marine et de l'armée de terre. Nos collections sont soigneusement conservées et présentées pour offrir aux visiteurs une expérience immersive et éducative.</p>
      </section>
      <AccueilGalerie />
      <section className="section-grid fade-in">
        <div className="section-item item-container">
          <img src={shakogrenadierImage} style={{ height: '30em', objectPosition: 'center' }} alt="Collections" />
          <h3>Ses Collections</h3>
          <p>Découvrez nos collections variées d'uniformes, d'équipements militaires, d'armes, de documents et d'œuvres d'art.</p>
          <a href="/pages/sescollections" className="btn btn-primary">En savoir plus</a>
        </div>
        <div className="section-item item-container">
          <img src={pseudoImage} style={{ height: '30em' }} alt="Conférences" />
          <h3>Ses Conférences</h3>
          <p>Participez à nos conférences sur divers thèmes liés à l'histoire militaire et coloniale.</p>
          <a href="/pages/sesconferences" className="btn btn-primary">En savoir plus</a>
        </div>
        <div className="section-item item-container">
          <img src={waterlooImage} style={{ height: '30em', objectPosition: 'top' }} alt="Actualités" />
          <h3>Ses Actualités</h3>
          <p>Restez informé des dernières nouvelles, événements et mises à jour concernant le musée et ses collections.</p>
          <button className="btn btn-primary" onClick={() => openPdfViewer(waterlooPdf)}>Lire l'article</button>
        </div>
      </section>
      <section className="content-section fade-in">
        <h2>Devenir Adhérent</h2>
        <p>Rejoignez l'Association des Amis du Musée des Troupes de Marine et soutenez nos missions. En devenant adhérent, vous contribuerez à la préservation et à la valorisation de notre patrimoine.</p>
        <a href="/pages/deveniradherent" className="btn btn-primary">Devenir Adhérent</a>
      </section>
      <VisiteWidget />
      <DonationWidget />

      {!isMobile && pdfViewerVisible && selectedPdfPath && (
        <div className="pdf-viewer-overlay" onClick={closePdfViewer}>
          <div className="pdf-viewer-container" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <button className="pdf-download-btn" onClick={downloadPdf}>Télécharger</button>
              <button className="close-pdf" onClick={closePdfViewer} aria-label="Fermer">&times;</button>
            </div>
            <div className="pdf-content">
              <iframe
                src={selectedPdfPath}
                className="pdf-viewer"
                title="Visualiseur PDF"
                key={selectedPdfPath}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Accueil;
